import React from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import './Table.css';

export type TableSortValue = string | number | Date | null | undefined;
export type TableSort = { key: string | null; direction: 'asc' | 'desc' };
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

function normalize(value: TableSortValue): string | number | null {
  if (value == null) return null;
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value.getTime() : null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const text = value.trim();
  if (!text || text === '—' || text === '–') return null;
  if (/^-?[$€£]?\d[\d,]*(\.\d+)?%?$/.test(text)) return Number(text.replace(/[$€£,%]/g, ''));
  return text;
}

/** Keep missing values last in either direction; never mutate the source rows. */
export function compareTableValues(left: TableSortValue, right: TableSortValue, direction: TableSort['direction'] = 'asc') {
  const a = normalize(left);
  const b = normalize(right);
  if (a === null) return b === null ? 0 : 1;
  if (b === null) return -1;
  const result = typeof a === 'number' && typeof b === 'number' ? a - b : collator.compare(String(a), String(b));
  return direction === 'asc' ? result : -result;
}

export function TableSortButton({ children, direction, onClick, label }: {
  children: React.ReactNode;
  direction?: TableSort['direction'];
  onClick: () => void;
  label?: string;
}) {
  const nextDirection = direction === 'asc' ? 'descending' : 'ascending';
  const SortIcon = direction === 'asc' ? ArrowUp : direction === 'desc' ? ArrowDown : ArrowUpDown;
  return (
    <button
      type="button"
      className="table__sort-button"
      data-sorted={direction ? 'true' : undefined}
      onClick={onClick}
      aria-label={label ? `${label}: sort ${nextDirection}` : undefined}
      title={`Sort ${nextDirection}`}
      data-activity-label="Sort table column"
    >
      {children}
      <SortIcon className="table__sort-icon" size={14} aria-hidden="true" />
    </button>
  );
}
